// Pure diff helpers for the Review interface's value-type-specific
// previews. The ReviewInterface used to render only cube-cell deltas
// from detail.deltas; for non-rate proposals (amounts / purposes /
// fees) deltas is empty and the reviewer saw an empty preview.
//
// These helpers mirror the server-side diffNonRateFields in
// catalogue-client.mjs but produce SPA-friendly structured shapes
// the Svelte template can iterate. Same rune-free split that
// guide-content-helpers.ts and proposal-timeline-helpers.ts use, so
// node:test can exercise them directly.

import type { ProductCatalogueFile } from './types';

export interface AmountRangeDiffRow {
  field: 'min' | 'max' | 'increment';
  before: number | undefined;
  after: number | undefined;
  delta: number | null;
  changed: boolean;
}

export function diffAmountRange(
  current: ProductCatalogueFile | null | undefined,
  candidate: ProductCatalogueFile | null | undefined
): AmountRangeDiffRow[] {
  const base = current?.loan_amount_range ?? {} as Record<string, number>;
  const cand = candidate?.loan_amount_range ?? {} as Record<string, number>;
  const out: AmountRangeDiffRow[] = [];
  for (const field of ['min', 'max', 'increment'] as const) {
    const before = (base as Record<string, number | undefined>)[field];
    const after = (cand as Record<string, number | undefined>)[field];
    const changed = before !== after;
    const delta = changed && typeof before === 'number' && typeof after === 'number'
      ? after - before
      : null;
    out.push({ field, before, after, delta, changed });
  }
  return out;
}

export interface PurposeDiffEntry {
  action: 'add' | 'remove' | 'update';
  purpose_id: string;
  before: LoanPurposeView | null;
  after: LoanPurposeView | null;
}

export interface LoanPurposeView {
  id: string;
  display_name: string;
  url_slug?: string;
  amount_range?: { min: number; max: number };
}

function purposesByIdView(catalogue: ProductCatalogueFile | null | undefined): Map<string, LoanPurposeView> {
  const list = (catalogue?.loan_purposes ?? []) as LoanPurposeView[];
  return new Map(list.map((p) => [p.id, p]));
}

function purposesEqual(a: LoanPurposeView, b: LoanPurposeView): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function diffPurposes(
  current: ProductCatalogueFile | null | undefined,
  candidate: ProductCatalogueFile | null | undefined
): PurposeDiffEntry[] {
  const base = purposesByIdView(current);
  const cand = purposesByIdView(candidate);
  const out: PurposeDiffEntry[] = [];
  for (const [id, beforePurpose] of base) {
    if (!cand.has(id)) {
      out.push({ action: 'remove', purpose_id: id, before: beforePurpose, after: null });
    }
  }
  for (const [id, afterPurpose] of cand) {
    if (!base.has(id)) {
      out.push({ action: 'add', purpose_id: id, before: null, after: afterPurpose });
    } else {
      const beforePurpose = base.get(id)!;
      if (!purposesEqual(beforePurpose, afterPurpose)) {
        out.push({ action: 'update', purpose_id: id, before: beforePurpose, after: afterPurpose });
      }
    }
  }
  return out;
}

export interface FeeDiffEntry {
  // Friendly path (the catalogue-client paths begin with "fees.";
  // strip that for display while keeping the canonical form for
  // diagnostics).
  path: string;
  before: unknown;
  after: unknown;
}

// Well-known fee paths. Mirrors FEE_PATHS in
// tools/catalogue-client.mjs.diffNonRateFields. New fee paths
// require an entry both there and here.
const FEE_PATHS = [
  'schedule_id',
  'establishment_fee',
  'dishonour_fee',
  'overdue_fee',
  'included_in_comparison_rate'
] as const;

export function diffFees(
  current: ProductCatalogueFile | null | undefined,
  candidate: ProductCatalogueFile | null | undefined
): FeeDiffEntry[] {
  const baseFees = (current?.fees ?? {}) as Record<string, unknown>;
  const candFees = (candidate?.fees ?? {}) as Record<string, unknown>;
  const out: FeeDiffEntry[] = [];
  for (const path of FEE_PATHS) {
    const before = baseFees[path];
    const after = candFees[path];
    if (before === undefined && after === undefined) continue;
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      out.push({ path, before, after });
    }
  }
  // Per-credit-rating establishment-fee overrides sit in the `fees`
  // value-type bucket (see GOVERNANCE-ARCHITECTURE.md §9.1) so they
  // belong in the same reviewer-facing diff as the product-level fee
  // schedule. Walk every credit-rating-axis-level node present on
  // either cube and surface any difference in
  // `rate_cube.{credit_rating_id}.establishment_fee_override`. The
  // full structural path keeps the reviewer's diagnostic context
  // intact alongside the shorter `establishment_fee` etc. entries.
  const baseCube = (current?.rate_cube ?? {}) as Record<string, { establishment_fee_override?: unknown }>;
  const candCube = (candidate?.rate_cube ?? {}) as Record<string, { establishment_fee_override?: unknown }>;
  const creditRatingIds = new Set<string>([
    ...Object.keys(baseCube),
    ...Object.keys(candCube)
  ]);
  for (const id of creditRatingIds) {
    const before = baseCube[id]?.establishment_fee_override;
    const after = candCube[id]?.establishment_fee_override;
    if (before === undefined && after === undefined) continue;
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      out.push({
        path: `rate_cube.${id}.establishment_fee_override`,
        before: before === undefined ? null : before,
        after: after === undefined ? null : after
      });
    }
  }
  return out;
}
